import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	IntercomConnectionSaveModel,
	IntercomConnectionViewModel,
	IntercomRegion,
	deleteConfiguration,
	getConfiguration,
	setConfiguration
} from "client/Api/Intercom/intercomConfiguration";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	Checkbox,
	Form,
	FormItem,
	SecretValueInput,
	Select,
	SelectItem,
	TextInput,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { SecretValue } from "client/Api/Apps/common";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const IntercomConnectionPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [isEnabled, setIsEnabled] = useState<boolean | null>(null);

	const [lastSavedModel, setLastSavedModel] = useState<OptionalModel<IntercomConnectionViewModel>>({ isSet: false });

	const [model, setModel] = useState<IntercomConnectionSaveModel>(defaultFormData);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	useActiveSettingsMenuItem(SettingsPageKind.Intercom);

	const formHandler: IFormHandler<IntercomConnectionSaveModel> = useFormHandler(
		model,
		setModel,
		validationConfiguration
	);

	const applyConfiguration = useCallback(
		(configuration: OptionalModel<IntercomConnectionViewModel>): void => {
			if (configuration.isSet) {
				setIsEnabled(true);

				const { appId, region } = configuration.value;

				setModel({
					appId,
					region,
					signingKey: {
						containsNewValue: false,
						value: null
					}
				});
			} else {
				setIsEnabled(false);
				setModel(defaultFormData);
			}
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const configuration: OptionalModel<IntercomConnectionViewModel> = await getConfiguration(signal)
						.catch(handlePageDataLoadingError);

					setLastSavedModel(configuration);
					applyConfiguration(configuration);
					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, applyConfiguration]
	);

	const cancelHandler = useCallback(
		(): void => {
			applyConfiguration(lastSavedModel);

			formHandler
				.validation.validate();
		},
		[
			applyConfiguration,
			formHandler,
			lastSavedModel
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;
			try {
				if (isEnabled) {
					const validationResult: InputValidationResult = await validation.validate(globalCancellation);

					if (validationResult.kind !== InputValidationResultKind.Valid) {
						setIsBusy(false);
						return;
					}

					await setConfiguration(model, globalCancellation);

					setLastSavedModel({
						isSet: true,
						value: {
							appId: model.appId,
							region: model.region
						}
					});
				} else {
					await deleteConfiguration(globalCancellation);
					setLastSavedModel({ isSet: false });
					setModel(defaultFormData);
				}

				alertManager.showSuccess(localize("_ChangesSaved"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!(isEnabled && validation.tryProcessServerError(e))) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isBusy,
			isEnabled,
			model
		]
	);

	const defaultActions = useMemo(
		(): readonly IntercomConnectionAction[] => listDefaultActions({
			cancel: cancelHandler,
			save: saveHandler
		}),
		[cancelHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly IntercomConnectionAction[] => {
			return buildActionsForTopBar<undefined, IntercomConnectionAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const enforceToSupplySigningKey = useCallback(
		(): void => {
			const attachable: IValueInputAttachable<SecretValue> = formHandler.for("signingKey");

			const { containsNewValue } = attachable.value;
			if (containsNewValue) {
				return;
			}

			attachable.reducer
				.setValue({
					containsNewValue: true,
					value: ""
				});

			attachable.validation
				.triggerInputFinished();
		},
		[formHandler]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_IntercomConnection")}
		>
			{(isEnabled !== null) && (
				<SettingsPage.Section>
					<Form
						onSubmit={saveHandler}
						hasWidthConstrained
					>

						<FormItem>
							<Checkbox
								text={localize("_EnableIntercom")}
								value={isEnabled}
								onChange={setIsEnabled}
							/>
						</FormItem>

						{isEnabled && (
							<>
								<FormItem label={localize("_AppId")} isRequired>
									<TextInput
										{...formHandler.for("appId")
											.attachToTextInput()
											.withOnChange(enforceToSupplySigningKey)
										}
									/>

									<ValidationError {...formHandler.for("appId").attachToValidationError()} />
								</FormItem>

								<FormItem label={localize("_Region")} isRequired>
									<Select
										{...formHandler.for("region").attachToSelect()}
										options={regionOptions}
										menuTitle={localize("_Region")}
									/>
								</FormItem>

								<FormItem label={localize("_SigningKey")} isRequired>
									<SecretValueInput
										{...formHandler.for("signingKey").attachToSecretValueInput()}
										containsInitialValue={lastSavedModel.isSet}
									/>

									<ValidationError
										{...formHandler.for("signingKey")
											.for("value")
											.attachToValidationError()
										}
									/>
								</FormItem>
							</>
						)}
					</Form>
				</SettingsPage.Section>
			)}
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
IntercomConnectionPage.displayName = "IntercomConnectionPage";

const defaultFormData: IntercomConnectionSaveModel = {
	appId: "",
	region: IntercomRegion.Eu,
	signingKey: {
		containsNewValue: true,
		value: ""
	}
};

const validationConfiguration = createValidationConfiguration<IntercomConnectionSaveModel>();

validationConfiguration.forScalar("appId")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forObject("signingKey")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

type IntercomConnectionActionContext = undefined;
type IntercomConnectionAction = ToolbarAction<IntercomConnectionActionContext>;

type DefaultActionsHandlers = {
	save: () => void;
	cancel: () => void;
};

const listDefaultActions = (
	handlers: DefaultActionsHandlers
): readonly IntercomConnectionAction[] => {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: () => true,
			execute: () => handlers.save(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: () => true,
			execute: () => handlers.cancel()
		}
	];
};

const regionOptions: readonly SelectItem<IntercomRegion>[] = [
	{
		value: IntercomRegion.Us,
		text: localize("_UnitedStates")
	},
	{
		value: IntercomRegion.Eu,
		text: localize("_Europe")
	},
	{
		value: IntercomRegion.Au,
		text: localize("_Australia")
	}
];

export const intercomConfigurationRouting = {
	page: "/IntercomConfiguration"
};
